import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlane, faEye } from "@fortawesome/free-solid-svg-icons";
import { Sparkles } from "lucide-react";
import Filter from "./BusinessTripFilter";
import Pagination from "../../../components/common/Pagination";
import BusinessTripDetailModal from "./BusinessTripDetailModal";
import PendingList from "./PendingBusinessTripList";
import CreateDrawer from "./BusinessTripCreateModal";
import {
  getAllBusinessTrips,
  getPendingBusinessTrips,
  approveBusinessTrip,
  rejectBusinessTrip,
  deleteBusinessTrip,
} from "../../../api/hrm/business_trips/businessTrip";
import {
  showInputDialog,
  showErrorToast,
  showSuccessToast,
} from "../../../components/ConfirmDialog";
import { formatDateVN } from "../../../utils/formatUtils";
import { downloadHrmListingExcel } from "../hrmListingExportExcel";
import { startBusinessTripListTour } from "./businessTripListTour";

const BusinessTripListPage = () => {
  const [filters, setFilters] = useState({});
  const [data, setData] = useState([]);
  const [pendingTrips, setPendingTrips] = useState([]);
  const [showDrawer, setShowDrawer] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [editData, setEditData] = useState(null);
  const [drawerMode, setDrawerMode] = useState("create");
  const [selectedTrip, setSelectedTrip] = useState(null);

  const storedUser = localStorage.getItem("user");
  const user = storedUser ? JSON.parse(storedUser) : null;

  // ========================
  // 🔹 Load danh sách công tác
  // ========================
  const fetchData = async () => {
    try {
      const res = await getAllBusinessTrips({ ...filters, currentPage, pageSize });
      setData(res.records || res || []);
      setTotalRecords(res.total || res.records?.length || 0);
    } catch (err) {
      console.error("Lỗi khi tải danh sách công tác:", err);
    }
  };

  // ========================
  // 🔹 Load danh sách chờ duyệt
  // ========================
  const fetchPendingTrips = async () => {
    try {
      const res = await getPendingBusinessTrips({ ...filters, page: currentPage, limit: pageSize });
      setPendingTrips(res.records || []);
    } catch (err) {
      console.error("Lỗi khi tải danh sách chờ duyệt:", err);
    }
  };

  useEffect(() => {
    // ✅ Chỉ gọi API khi bộ lọc đã khởi tạo đủ ngày bắt đầu & kết thúc
    if (filters.from_date && filters.to_date) {
      fetchData();
      fetchPendingTrips();
    }
  }, [filters, currentPage, pageSize]);

  // ========================
  // 🔹 Phê duyệt / Từ chối
  // ========================
  const handleApprovedTrip = async (trip) => {
    try {
      await approveBusinessTrip(trip.id, user.id);
      showSuccessToast("✅ Đã phê duyệt phiếu công tác");
      fetchPendingTrips();
      fetchData();
    } catch (err) {
      console.error("Lỗi phê duyệt:", err);
      showErrorToast("Không thể phê duyệt phiếu.");
    }
  };

  const handleRejectTrip = async (trip) => {
    try {
      const reason = await showInputDialog({
        title: "Từ chối công tác",
        inputLabel: "Nhập lý do từ chối:",
        confirmText: "Từ chối",
      });
      if (!reason) return;
      await rejectBusinessTrip(trip.id, user.id, reason);
      showSuccessToast("🚫 Đã từ chối phiếu công tác");
      fetchPendingTrips();
      fetchData();
    } catch (err) {
      console.error("Lỗi từ chối:", err);
      showErrorToast("Không thể từ chối phiếu.");
    }
  };

  const parseMonthKey = (ym) => {
    if (!ym || typeof ym !== "string" || !/^\d{4}-\d{2}$/.test(ym)) return null;
    const [yStr, mStr] = ym.split("-");
    const year = parseInt(yStr, 10);
    const month = parseInt(mStr, 10);
    if (Number.isNaN(year) || Number.isNaN(month) || month < 1 || month > 12)
      return null;

    const pad = (n) => String(n).padStart(2, "0");
    const lastDay = new Date(year, month, 0).getDate();
    const monthPadded = pad(month);

    return {
      year,
      month,
      monthPadded,
      from_date: `${year}-${monthPadded}-01`,
      to_date: `${year}-${monthPadded}-${pad(lastDay)}`,
    };
  };

  const handleDownloadCsv = async ({
    branch_id,
    month,
    branch_label,
  } = {}) => {
    try {
      const parsedMonth = month ? parseMonthKey(month) : null;
      const from_date = parsedMonth ? parsedMonth.from_date : filters.from_date;
      const to_date = parsedMonth ? parsedMonth.to_date : filters.to_date;

      if (!from_date || !to_date) {
        showErrorToast("Chọn khoảng thời gian (từ ngày – đến ngày).");
        return;
      }

      const query = {
        ...filters,
        currentPage: 1,
        pageSize: 10000,
        from_date,
        to_date,
      };
      if (branch_id) query.branch_id = branch_id;

      const res = await getAllBusinessTrips(query);
      const allData = res?.records || res || [];

      if (allData.length === 0) {
        showErrorToast("Không có dữ liệu để xuất.");
        return;
      }

      const header = [
        "STT",
        "Tên nhân viên",
        "Phòng ban",
        "Chi nhánh",
        "Địa điểm công tác",
        "Từ ngày",
        "Đến ngày",
        "Lý do công tác",
        "Ngân sách dự kiến",
        "Trạng thái",
      ];

      const rows = allData.map((item, index) => [
        index + 1,
        item.employee_name ?? "",
        item.department_name ?? "",
        item.branch_name ?? "",
        item.location ?? "",
        item.start_date ? formatDateVN(item.start_date) : "",
        item.end_date ? formatDateVN(item.end_date) : "",
        (item.reason || "").replace(/\\n/g, " "),
        item.budget_cost
          ? new Intl.NumberFormat("vi-VN").format(item.budget_cost)
          : "",
        item.status ?? "",
      ]);

      const aoa = [header, ...rows];

      const branchText =
        branch_label?.trim() ||
        (branch_id ? "Chi nhánh" : "Tất cả chi nhánh");
      const branchTag = branch_id ? String(branch_id).slice(0, 8) : "tat-ca";

      const title = parsedMonth
        ? `Dữ liệu công tác tháng ${parsedMonth.monthPadded} năm ${parsedMonth.year} của ${branchText}`
        : `Dữ liệu công tác từ ${formatDateVN(from_date)} đến ${formatDateVN(to_date)}`;

      const filename = parsedMonth
        ? `cong-tac_${month}_${branchTag}.xlsx`
        : `cong-tac_${from_date}_${to_date}_${branchTag}.xlsx`;

      await downloadHrmListingExcel(aoa, {
        title,
        sheetName: "CongTac",
        filename,
        columnWidths: [6, 22, 18, 18, 22, 12, 12, 40, 20, 18],
      });

      showSuccessToast("Đã tải file Excel.");
    } catch (err) {
      console.error("❌ Lỗi export Excel:", err);
      showErrorToast("Không thể xuất Excel.");
    }
  };

  const handleDelete = async (trip) => {
    try {
      await deleteBusinessTrip(trip.id);
      showSuccessToast("Đã xóa phiếu công tác!");
      fetchData();
    } catch (err) {
      showErrorToast("Không thể xóa phiếu công tác.");
    }
  };
  
  
  // ========================
  // 🔹 Event handler
  // ========================
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  // ========================
  // 🔹 Render
  // ========================
  return (
    <div className="p-4">
      {/* HEADER */}
      <div className="flex items-center justify-between mb-8" data-tour="hrm-business-header">
        <div className="flex items-center gap-4">
          <div className="bg-red-700 p-2.5 rounded-2xl text-white shadow-lg shadow-red-200">
            <FontAwesomeIcon
              icon={faPlane}
              className="text-xl w-6 h-6 flex items-center justify-center"
            />
          </div>

          <div>
            <h1 className="text-xl font-black text-gray-800 tracking-tight uppercase">
              Quản lý <span className="text-red-700">công tác</span>
            </h1>

            <div className="flex items-center gap-2 mt-0.5">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
              <p className="text-[11px] text-gray-500 font-bold uppercase tracking-wider text-nowrap">
                <span className="text-red-600">{totalRecords}</span>{" "} phiếu công tác đã đăng ký
              </p>
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => window.setTimeout(() => startBusinessTripListTour(), 80)}
          title="Hướng dẫn nhanh"
          aria-label="Hướng dẫn nhanh"
          className="group relative inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-red-200 bg-white text-red-700 shadow-sm transition-all hover:bg-red-50 hover:border-red-300"
        >
          <Sparkles className="h-5 w-5" strokeWidth={2.2} />
          <span className="pointer-events-none absolute bottom-full right-0 mb-2 rounded-lg border border-red-100 bg-white px-2.5 py-1 text-[11px] font-bold text-red-700 whitespace-nowrap opacity-0 translate-y-1 shadow-md transition-all group-hover:opacity-100 group-hover:translate-y-0">
            Hướng dẫn nhanh
          </span>
        </button>
      </div>

      {/* Bộ lọc */}
      <div data-tour="hrm-business-filter">
        <Filter
          onNewClick={() => {
            setEditData(null);
            setDrawerMode("create");
            setShowDrawer(true);
          }}
          onFilterChange={handleFilterChange}
          onDownloadCsv={handleDownloadCsv}
        />
      </div>

      {/* Danh sách chờ duyệt */}
      <div data-tour="hrm-business-pending">
        <PendingList
          data={pendingTrips}
          currentUserId={user?.id}
          currentUserRole={user?.role}
          onApproved={handleApprovedTrip}
          onReject={handleRejectTrip}
          onDelete={handleDelete}
        />
      </div>

      <h2 className="font-semibold text-base text-gray-700 mb-3 ml-2">
        Bảng danh sách phiếu công tác ({totalRecords} phiếu)
      </h2>

      {/* Bảng dữ liệu */}
      <div className="border rounded-md overflow-x-auto" data-tour="hrm-business-table">       
        <table className="w-full table-auto text-sm bg-white">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-3 py-2 text-center min-w-[50px] "></th>
              <th className="px-3 py-2 text-gray-600 font-semibold text-center min-w-[50px] ">#</th>
              <th className="px-3 py-2 text-gray-600 font-semibold min-w-[150px]">Chi nhánh</th>
              <th className="px-3 py-2 text-gray-600 font-semibold min-w-[150px]">Nhân viên</th>
              <th className="px-3 py-2 text-gray-600 font-semibold min-w-[120px]">Bộ phận</th>
              <th className="px-3 py-2 text-gray-600 font-semibold text-center min-w-[170px]">Thời gian</th>
              <th className="px-3 py-2 text-gray-600 font-semibold text-center min-w-[100px]">Số ngày</th>
              <th className="px-3 py-2 text-gray-600 font-semibold min-w-[150px]">Địa điểm</th>
              <th className="px-3 py-2 text-gray-600 font-semibold min-w-[200px]">Lý do</th>
              <th className="px-3 py-2 text-gray-600 font-semibold text-right min-w-[140px]">Ngân sách</th>
              <th className="px-3 py-2 text-gray-600 font-semibold text-center min-w-[140px]">Trạng thái</th>
            </tr>
          </thead>

          <tbody>
            {data.length === 0 ? (
              <tr>
                <td colSpan={11} className="text-center py-4 text-gray-500">
                  Không có dữ liệu.
                </td>
              </tr>
            ) : (
              data.map((item, index) => (
                <tr key={item.id} className="border-t hover:bg-gray-50">
                  {/* 👁️ Xem */}
                  <td className="px-3 py-3 text-center">
                    <button
                      onClick={() => setSelectedTrip(item)}
                      className="text-gray-400 hover:text-red-800 transition"
                      title="Xem chi tiết"
                    >
                      <FontAwesomeIcon icon={faEye} />
                    </button>
                  </td>

                  {/* STT */}
                  <td className="px-3 py-3 text-center">
                    {(currentPage - 1) * pageSize + index + 1}
                  </td>

                  <td className="px-3 py-3">{item.branch_name}</td>
                  <td className="px-3 py-3 font-semibold">{item.employee_name}</td>
                  <td className="px-3 py-3">{item.department_name || "-"}</td>
                  <td className="px-3 py-3 text-center">
                    <span className="bg-indigo-100 text-indigo-700 px-2 py-1 rounded-full inline-block mb-1">
                      Từ {formatDateVN(item.start_date)}
                    </span>
                    <br />
                    <span className="bg-slate-100 text-slate-700 px-2 py-1 rounded-full inline-block">
                      đến {formatDateVN(item.end_date)}
                    </span>
                  </td>
                
                  <td className="px-3 py-3 text-center">
                    {(() => {
                      if (!item.start_date || !item.end_date) return "-";
                      const start = new Date(item.start_date);
                      const end = new Date(item.end_date);
                      const diffDays = Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;

                      // 🎨 Chia màu theo mức độ
                      let colorClass = "";
                      if (diffDays <= 2) {
                        colorClass = "bg-green-50 text-green-700 border-green-200";
                      } else if (diffDays < 5) {
                        colorClass = "bg-yellow-50 text-yellow-700 border-yellow-200";
                      } else {
                        colorClass = "bg-red-50 text-red-700 border-red-200";
                      }

                      return (
                        <span
                          className={`inline-block min-w-[60px] px-3 py-1 rounded-full text-sm font-medium border ${colorClass}`}
                        >
                          {diffDays} ngày
                        </span>
                      );
                    })()}
                  </td>

                  <td className="px-3 py-3">{item.location}</td>
                  <td className="px-3 py-3">{item.reason}</td>

                  {/* Ngân sách */}
                  <td className="px-3 py-3 text-right font-semibold">
                    {Number(item.budget_cost).toLocaleString("vi-VN", {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}{" "}
                    ₫
                  </td>

                  {/* Trạng thái */}
                  <td className="px-3 py-3 text-center">
                    {item.status === "Đã duyệt" ? (
                      <span className="text-xs font-medium bg-green-100 text-green-700 border border-green-300 px-2 py-1 rounded-full">
                        {item.status}
                      </span>
                    ) : item.status === "Không duyệt" ? (
                      <span className="text-xs font-medium bg-rose-100 text-rose-700 border border-rose-300 px-2 py-1 rounded-full">
                        {item.status}
                      </span>
                    ) : (
                      <span className="text-xs font-medium bg-yellow-100 text-yellow-700 border border-yellow-300 px-2 py-1 rounded-full">
                        {item.status || "Chờ duyệt"}
                      </span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <BusinessTripDetailModal
          isOpen={!!selectedTrip}
          onClose={() => setSelectedTrip(null)}
          data={selectedTrip}
        />
      </div>

      <Pagination
        totalRecords={totalRecords}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      <CreateDrawer
        isOpen={showDrawer}
        onClose={() => setShowDrawer(false)}
        onSaved={() => {
          fetchData();
          fetchPendingTrips();
          setShowDrawer(false);
        }}
        initialData={editData}
        mode={drawerMode}
      />
    </div>
  );
};

export default BusinessTripListPage;
